import React from 'react';

export interface BeforeAfterRow {
  label: string;
  before: string;
  after: string;
}

interface BeforeAfterConfirmProps {
  rows: BeforeAfterRow[];
  /** Forwarded so a host input can wire aria-describedby to this preview. */
  id?: string;
  /** Optional extra content (e.g. a comment textarea) rendered below the rows. */
  children?: React.ReactNode;
}

/**
 * LR-484: shared before -> after confirmation preview, promoting the copy-pasted
 * LR-478 S3 idiom (Terminate/Notice/SettlementDate/PartialRecall) into one component.
 * Renders a role="note" aria-live box with one row per changed field: label, the old
 * value in muted text, " -> ", and the new value emphasised.
 */
export const BeforeAfterConfirm: React.FC<BeforeAfterConfirmProps> = ({ rows, id, children }) => (
  <div
    id={id}
    role="note"
    aria-live="polite"
    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700"
  >
    {rows.length === 0 ? (
      <p className="text-gray-500">No changes to confirm.</p>
    ) : (
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.label}>
            <span className="text-gray-600">{r.label}:</span>{' '}
            <span className="text-gray-500">{r.before}</span>{' '}
            <span aria-hidden="true">→</span>{' '}
            <span className="font-semibold text-gray-900">{r.after}</span>
          </li>
        ))}
      </ul>
    )}
    {children}
  </div>
);
